/**
 * Shop layout wrapper.
 *
 * Works for both authenticated users (shows full Header with nav) and
 * unauthenticated visitors (shows minimal branded header with logo only).
 * Cart context is provided here so all shop child routes have access.
 * Includes the slide-out CartDrawer.
 */
import React from 'react';
import {Outlet, Link} from 'react-router-dom';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useCart} from '../context/CartContext';
import Header from '@/shared/layout/Header';
import Banner from '@/shared/components/Banner';
import {ThemedLogo} from '@/shared/themed/ThemedLogo';
import {ShoppingCart} from 'lucide-react';
import CartDrawer from '../components/CartDrawer';

/** Minimal header for unauthenticated visitors — logo + cart icon only. */
const PublicShopHeader: React.FC = () => {
    return (
        <header className="bg-mld-menu-background shadow-sm">
            <div className="container mx-auto px-4 lg:px-6 py-4 flex justify-between items-center">
                <Link to="/shop">
                    <ThemedLogo className="h-8" />
                </Link>
                <CartBadge />
            </div>
        </header>
    );
};

/** Cart icon with badge count — click toggles the drawer. */
function CartBadge() {
    const {itemCount, toggleDrawer} = useCart();
    return (
        <button
            type="button"
            onClick={toggleDrawer}
            className="relative text-mld-menu-text hover:text-mld-primary transition-colors">
            <ShoppingCart className="h-6 w-6" />
            {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center rounded-full bg-mld-tertiary text-white text-[10px] font-bold">
                    {itemCount > 99 ? '99+' : itemCount}
                </span>
            )}
        </button>
    );
}

const ShopLayoutInner: React.FC = () => {
    const {user} = useAuth();
    const isLoggedIn = user?.isLoggedIn ?? false;

    return (
        <div className="bg-mld-background min-h-screen">
            {isLoggedIn ? <Header rightAction={<CartBadge />} /> : <PublicShopHeader />}
            <Banner />
            <main className="container mx-auto px-4 py-12">
                <Outlet />
            </main>
            <CartDrawer />
        </div>
    );
};

/**
 * CartProvider lives at the App root now (so the patient dashboard
 * hero can also push lines into the cart), so this layout just renders
 * the inner shell directly.
 */
const ShopLayout: React.FC = () => <ShopLayoutInner />;

export default ShopLayout;
